import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { dollarsToCurrency } from '@/utils/currencyHelpers'
import { Button, Text, Popover } from '@/ui'
import { FEATURE_FLAG_KEYS } from '@/flags'
import {
  FILING_STATUSES,
  SAFE_HARBOR_HIGH_INCOME_LIMIT_IN_DOLLARS,
  SAFE_HARBOR_HIGH_INCOME_LIMIT_MFS_IN_DOLLARS,
} from '@/features/taxes/taxConstants'
import { TypographyStyles } from '@/styles'

export enum CALCULATE_SAFE_HARBOR_STEP {
  calculateSafeHarborIntro = 'calculateSafeHarborIntro',
  quarterlyTaxRecommendations = 'quarterlyTaxRecommendations',
  totalTaxesAndIncome = 'totalTaxesAndIncome',
  priorQuarterPayments = 'priorQuarterPayments',
  finish = 'finish',
}

const HIGH_INCOME_MULTIPLIER = 1.1
const BASE_MULTIPLIER = 1

/**
 * Married filing separately carries a lower limit than the other statuses. A
 * missing status is treated as not MFS: it is required at setup, so absence is
 * a gap in the record rather than a signal.
 */
export const safeHarborHighIncomeLimitInDollars = (
  filingStatus?: FILING_STATUSES | null
): number =>
  filingStatus === FILING_STATUSES.married_filing_separately
    ? SAFE_HARBOR_HIGH_INCOME_LIMIT_MFS_IN_DOLLARS
    : SAFE_HARBOR_HIGH_INCOME_LIMIT_IN_DOLLARS

/** The share of last year's federal tax the IRS requires for safe harbor. */
export const safeHarborMultiplier = ({
  adjustedGrossIncomeInDollars,
  filingStatus,
}: {
  adjustedGrossIncomeInDollars: number | null
  filingStatus?: FILING_STATUSES | null
}): number =>
  Number(adjustedGrossIncomeInDollars) >
  safeHarborHighIncomeLimitInDollars(filingStatus)
    ? HIGH_INCOME_MULTIPLIER
    : BASE_MULTIPLIER

export const calculateSafeHarbor = ({
  adjustedGrossIncomeInDollars,
  totalFederalTaxesInDollars,
  filingStatus,
}: {
  adjustedGrossIncomeInDollars: number | null
  totalFederalTaxesInDollars: number | null
  filingStatus?: FILING_STATUSES | null
}) => {
  if (!adjustedGrossIncomeInDollars || !totalFederalTaxesInDollars) {
    return null
  }

  return dollarsToCurrency(totalFederalTaxesInDollars)
    .multiply(
      safeHarborMultiplier({ adjustedGrossIncomeInDollars, filingStatus })
    )
    .divide(4)
    .dollars()
}

export const SafeHarborTooltip = () => (
  <>
    <Text variant={TypographyStyles.H3}>Safe Harbor Estimate</Text>
    <br />
    <Text>
      The term “safe harbor” means you are protected from penalties by law as
      long as certain conditions are met.
    </Text>
    <br />
    <Text>
      It is calculated by taking your prior year tax liability and dividing by
      4. For example, if you paid $1,000 last year for federal taxes,
      you&apos;ll divide it by 4 and pay $250 for this quarter&apos;s tax
      payment. You&apos;ll do the same calculation for your state quarterly tax
      payment (if it applies). To reduce the risk of an underpayment penalty,
      make sure to pay at least 90% of what you owe for the current year or 100%
      of what you owe for the prior year.
    </Text>
  </>
)

/**
 * Catching up on elapsed quarters only makes sense once always-on QTE is live.
 * Before that the checklist closes on the IRS due date, so a late joiner never
 * reaches a prior quarter's estimate to answer for it.
 */
export const useSafeHarborCatchUpEnabled = () =>
  useBooleanFlagValue(FEATURE_FLAG_KEYS.alwaysOnQteEnabled, false)

export const useCalculateSafeHarborSteps = (
  currentStep: CALCULATE_SAFE_HARBOR_STEP,
  targetQuarter?: string | null
) => {
  const [_, setSearchParams] = useSearchParams()
  const catchUpEnabled = useSafeHarborCatchUpEnabled()

  // Q1 has no elapsed quarters to ask about, and a step left in the array
  // that renders nothing is still a stop the member cannot move past. An
  // absent quarter fails the comparison on its own, so there is nothing to
  // stand in for it.
  const hasPriorQuarterStep = catchUpEnabled && Number(targetQuarter) > 1

  const goToStep = useCallback(
    (newStep: 1 | -1 | CALCULATE_SAFE_HARBOR_STEP) => {
      if (typeof newStep === 'string') {
        setSearchParams({ step: newStep })
        return
      }

      // Use updated financial profile to calculate next step.  Default to current fp state
      const steps = [
        CALCULATE_SAFE_HARBOR_STEP.calculateSafeHarborIntro,
        CALCULATE_SAFE_HARBOR_STEP.quarterlyTaxRecommendations,
        CALCULATE_SAFE_HARBOR_STEP.totalTaxesAndIncome,
        ...(hasPriorQuarterStep
          ? [CALCULATE_SAFE_HARBOR_STEP.priorQuarterPayments]
          : []),
        CALCULATE_SAFE_HARBOR_STEP.finish,
      ]

      const currentStepIndex = steps.indexOf(currentStep)

      // Go forward or backward in stack
      setSearchParams({ step: steps[currentStepIndex + newStep] })
    },
    [currentStep, hasPriorQuarterStep, setSearchParams]
  )

  const goToNextStep = useCallback(
    (step?: CALCULATE_SAFE_HARBOR_STEP) => goToStep(step || 1),
    [goToStep]
  )

  const goToPreviousStep = useCallback(() => goToStep(-1), [goToStep])

  return { goToNextStep, goToPreviousStep, hasPriorQuarterStep }
}

export const WhatIsSafeHarborPopup = ({
  variant = 'discreet',
}: {
  variant?: 'discreet' | 'link'
}) => (
  <Popover>
    <Popover.Trigger asChild>
      <Button
        variant={variant}
        className={variant === 'link' ? 'p-0 h-auto' : undefined}
      >
        What is a safe harbor estimate?
      </Button>
    </Popover.Trigger>
    <Popover.Content className="max-w-[400px]">
      <SafeHarborTooltip />
    </Popover.Content>
  </Popover>
)
